import {
  BadgeCheck,
  Banknote,
  Bot,
  CalendarDays,
  Check,
  ChevronDown,
  CircleAlert,
  Download,
  FileArchive,
  FileSpreadsheet,
  FileText,
  History,
  KeyRound,
  Link2,
  Loader2,
  LockKeyhole,
  LogOut,
  Pencil,
  Plus,
  RefreshCcw,
  Search,
  ShieldCheck,
  UserPlus,
  UploadCloud,
  WalletCards,
  X,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { createPortal } from "react-dom";
import MonthSelect from "./MonthSelect";


const formatYen = (value) => `¥${Number(value || 0).toLocaleString("ja-JP")}`;

const getSettlementKindLabel = (item = {}) => (
  item.settlement_type === "invoice_request"
    ? "请求书"
    : item.settlement_type === "additional_expense"
    ? (item.expense_category_label || "附加费用")
    : "主结算"
);

const formatAccount = (account = {}) => {
  const hasBankDetails = [
    account.bank_name,
    account.branch_name,
    account.branch_code,
    account.account_number,
    account.account_holder,
  ].some(Boolean);
  if (!hasBankDetails) return "未填写银行账户";
  return [
    account.bank_name,
    account.branch_name,
    account.branch_code && `(${account.branch_code})`,
    account.account_type,
    account.account_number,
    account.account_holder,
  ].filter(Boolean).join(" · ");
};

export default function AdminSettlements({ settlementProps, user, onLogout }) {
  const {
    data,
    error,
    exporting,
    financeAccounts,
    financeForm,
    invoiceRequestDownloadMonth,
    invoiceRequestDownloading,
    invoiceRequestForm,
    invoiceRequestUploading,
    invoiceOrderSearch,
    invoiceOrderSearchResults,
    invoiceOrderSearching,
    invoiceLinkModal,
    invoiceLinkSaving,
    invoiceLinkSearch,
    invoiceLinkSearchResults,
    invoiceLinkSearching,
    invoiceLinkSelectedOrderIds,
    isAdmin,
    isFinance,
    loading,
    month,
    orderSearch,
    orderSearchResults,
    orderSearching,
    search,
    selectedInvoiceRequestIds,
    selectedInvoiceOrderIds,
    transferDate,
    transferTarget,
    todayJapan,
    confirmTransfer,
    clearInvoiceLinkOrderSelection,
    closeInvoiceOrderLinker,
    createFinanceAccount,
    downloadInvoiceRequest,
    downloadMonthlyInvoiceRequests,
    downloadSelectedInvoiceRequests,
    exportMonthlyPaymentStatements,
    exportMonthlySettlements,
    fetchSettlements,
    openTransferConfirmation,
    openPendingSettlementEdit,
    openAdditionalExpense,
    openInvoiceOrderLinker,
    resetFinancePassword,
    setFinanceForm,
    setInvoiceRequestDownloadMonth,
    setInvoiceRequestFile,
    setInvoiceRequestForm,
    setInvoiceOrderSearch,
    setInvoiceLinkSearch,
    setMonth,
    setOrderSearch,
    setSearch,
    setTransferDate,
    setTransferTarget,
    toggleFinanceAccount,
    toggleInvoiceRequestSelection,
    toggleInvoiceOrderSelection,
    toggleInvoiceLinkOrderSelection,
    unlockSettlement,
    voidPendingSettlement,
    searchSettlementOrders,
    searchInvoiceRequestOrders,
    searchInvoiceLinkOrders,
    saveInvoiceOrderLinks,
    submitInvoiceRequest,
  } = settlementProps;

  return (
    <section className="space-y-5" data-testid="admin-settlement-page">
      <header className="flex flex-col gap-3 border-b border-border-subtle pb-5 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase text-primary">Finance Settlement</p>
          <h1 className="mt-1 text-2xl font-semibold">司机订单结算</h1>
          <p className="mt-1 break-words text-sm text-muted-foreground">财务确认转账后自动归入实际转账月份，原派单价格永久保留。</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <span className="premium-chip inline-flex min-w-0 flex-1 items-center gap-1.5 sm:flex-none">
            <ShieldCheck className="h-3.5 w-3.5" />
            <span className="truncate">{isFinance ? "财务账号" : "管理员"} · {user?.name || user?.email}</span>
          </span>
          {isFinance && (
            <button type="button" onClick={onLogout} className="premium-button shrink-0 whitespace-nowrap border border-border bg-background px-3 py-2 text-sm">
              <LogOut className="h-4 w-4" />
              退出
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          <CircleAlert className="h-4 w-4" />
          {error}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile label="待转账" value={`${data.summary?.pending_count || 0}件`} amount={data.summary?.pending_amount} tone="amber" />
        <SummaryTile label={`${month} 已转账`} value={`${data.summary?.paid_count || 0}件`} amount={data.summary?.paid_amount} tone="emerald" />
        <SummaryTile label="当前权限" value={isFinance ? "确认转账" : "创建 / 审计"} icon={LockKeyhole} />
        <SummaryTile label="归档规则" value="按实际转账日" icon={CalendarDays} />
      </div>

      {isAdmin && (
        <section className="premium-panel overflow-hidden" data-testid="invoice-request-upload-panel">
          <div className="border-b border-border-subtle px-4 py-4">
            <h2 className="flex items-center gap-2 font-semibold"><UploadCloud className="h-4 w-4 text-primary" />上传请求书</h2>
            <p className="mt-1 text-xs text-muted-foreground">直接上传文件并填写金额即可；如有需要，可搜索并多选绑定订单。提交后立即进入待财务处理，不改动任何订单资料或结算状态。</p>
          </div>
          <form onSubmit={submitInvoiceRequest} className="grid gap-3 p-4 lg:grid-cols-12 lg:items-end">
            <label className="text-xs font-medium text-muted-foreground lg:col-span-2">
              请求书金额 <span className="text-destructive">*</span>
              <input
                type="number"
                min="1"
                step="1"
                value={invoiceRequestForm.settlement_amount}
                onChange={(event) => setInvoiceRequestForm((current) => ({ ...current, settlement_amount: event.target.value }))}
                className="premium-input mt-1"
                placeholder="日元"
                required
              />
            </label>
            <label className="text-xs font-medium text-muted-foreground lg:col-span-2">
              请求书月份
              <MonthSelect
                value={invoiceRequestForm.request_month}
                onChange={(value) => setInvoiceRequestForm((current) => ({ ...current, request_month: value }))}
                className="premium-input mt-1"
              />
            </label>
            <label className="text-xs font-medium text-muted-foreground lg:col-span-5">
              请求书文件 <span className="text-destructive">*</span>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={(event) => setInvoiceRequestFile(event.target.files?.[0] || null)}
                className="mt-1 block w-full text-xs text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-primary/10 file:px-3 file:py-2 file:font-semibold file:text-primary"
                required
              />
            </label>
            <button type="submit" disabled={invoiceRequestUploading} className="premium-button bg-primary px-4 py-2 text-primary-foreground lg:col-span-2">
              {invoiceRequestUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
              {invoiceRequestUploading ? "上传中" : "上传并提交"}
            </button>
            <label className="text-xs font-medium text-muted-foreground lg:col-span-12">
              备注（可选）
              <input
                value={invoiceRequestForm.note}
                onChange={(event) => setInvoiceRequestForm((current) => ({ ...current, note: event.target.value }))}
                className="premium-input mt-1"
                placeholder="例如：2026年8月请求书"
              />
            </label>
            <div className="rounded-xl border border-border-subtle bg-background/40 p-3 lg:col-span-12" data-testid="invoice-request-order-linker">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <label className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input
                    value={invoiceOrderSearch}
                    onChange={(event) => setInvoiceOrderSearch(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        searchInvoiceRequestOrders();
                      }
                    }}
                    className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground"
                    placeholder="可选：搜索司机、订单号或订单关键字"
                  />
                </label>
                <button type="button" onClick={searchInvoiceRequestOrders} disabled={invoiceOrderSearching} className="premium-button border border-primary/30 bg-primary/10 px-4 py-2 text-primary">
                  {invoiceOrderSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  搜索订单
                </button>
                <span className="text-xs font-medium text-muted-foreground">已绑定 {selectedInvoiceOrderIds.length} 个</span>
              </div>
              {invoiceOrderSearchResults.length > 0 && (
                <div className="mt-3 max-h-64 divide-y divide-border-subtle overflow-y-auto rounded-lg border border-border-subtle">
                  {invoiceOrderSearchResults.map((order) => (
                    <label key={order.id} className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-primary/5">
                      <input
                        type="checkbox"
                        checked={selectedInvoiceOrderIds.includes(order.id)}
                        onChange={() => toggleInvoiceOrderSelection(order.id)}
                        className="mt-1 h-4 w-4 rounded border-border accent-primary"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2 text-sm">
                          <strong>{order.order_no}</strong>
                          {order.driver_name && <span className="text-muted-foreground">司机：{order.driver_name}</span>}
                          {order.customer_name && <span className="text-muted-foreground">客户：{order.customer_name}</span>}
                        </span>
                        <span className="mt-0.5 block break-words text-xs text-muted-foreground">{order.departure_date} {order.pickup_time} · {order.route?.summary || "未填写路线"}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="mt-2 text-[11px] text-muted-foreground">绑定订单完全可选，可同时勾选多个；只建立查询关联，不会修改订单。</p>
            </div>
            <p className="text-[11px] text-muted-foreground lg:col-span-12">支持 PDF、JPG、PNG、WEBP、HEIC，单个文件最大25MB。</p>
          </form>
        </section>
      )}

      {isAdmin && (
        <section className="premium-panel overflow-hidden" data-testid="additional-expense-order-search">
          <div className="border-b border-border-subtle px-4 py-4">
            <h2 className="flex items-center gap-2 font-semibold"><Plus className="h-4 w-4 text-primary" />单独结算停车费、加班费等</h2>
            <p className="mt-1 text-xs text-muted-foreground">按订单号或订单资料找到订单后，新增一笔直接付给司机的附加费用；不会修改原派单价、主结算金额或原订单结算状态。</p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <label className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input
                  value={orderSearch}
                  onChange={(event) => setOrderSearch(event.target.value)}
                  onKeyDown={(event) => event.key === "Enter" && searchSettlementOrders()}
                  className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground"
                  placeholder="订单号、司机、日期或路线；留空可看最近订单"
                />
              </label>
              <button type="button" onClick={searchSettlementOrders} disabled={orderSearching} className="premium-button bg-primary px-4 py-2 text-primary-foreground">
                {orderSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                查找订单
              </button>
            </div>
          </div>
          {orderSearchResults.length > 0 ? (
            <div className="divide-y divide-border-subtle">
              {orderSearchResults.map((order) => (
                <div key={order.id} className="flex flex-col gap-3 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{order.order_no}</strong>
                      <span className="text-sm text-muted-foreground">{order.driver_name}</span>
                      {order.has_order_settlement && <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600">主结算已登记</span>}
                      {order.additional_expense_count > 0 && <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-600">已有附加费用 {order.additional_expense_count} 笔</span>}
                    </div>
                    <p className="mt-1 break-words text-xs text-muted-foreground">{order.departure_date} {order.pickup_time} · {order.route?.summary}</p>
                  </div>
                  <button type="button" onClick={() => openAdditionalExpense(order)} className="premium-button shrink-0 border border-primary/30 bg-primary/10 px-4 py-2 text-sm text-primary">
                    <Plus className="h-4 w-4" />新增附加费用
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-5 py-6 text-center text-sm text-muted-foreground">输入条件后查找可结算订单</div>
          )}
        </section>
      )}

      <section className="premium-panel overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-border-subtle px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 font-semibold"><Banknote className="h-4 w-4 text-amber-500" />待财务处理</h2>
            <p className="mt-1 text-xs text-muted-foreground">核对金额与已填写的银行资料后，再确认已转账。</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-[11px] font-medium text-muted-foreground">
              请求书月份
              <MonthSelect
                value={invoiceRequestDownloadMonth}
                onChange={setInvoiceRequestDownloadMonth}
                className="mt-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={downloadMonthlyInvoiceRequests}
              disabled={Boolean(invoiceRequestDownloading)}
              className="premium-button border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
            >
              {invoiceRequestDownloading === "month" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileArchive className="h-4 w-4" />}
              下载整月请求书
            </button>
            <button
              type="button"
              onClick={downloadSelectedInvoiceRequests}
              disabled={!selectedInvoiceRequestIds.length || Boolean(invoiceRequestDownloading)}
              className="premium-button border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary disabled:cursor-not-allowed disabled:opacity-40"
            >
              {invoiceRequestDownloading === "selected" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              下载已选（{selectedInvoiceRequestIds.length}）
            </button>
            <button type="button" onClick={fetchSettlements} disabled={loading} className="premium-button border border-border bg-background px-3 py-2 text-sm">
              <RefreshCcw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />刷新
            </button>
          </div>
        </div>
        <SettlementTable
          items={data.pending || []}
          empty="暂无待转账记录"
          isPending
          isFinance={isFinance}
          isAdmin={isAdmin}
          onMarkPaid={openTransferConfirmation}
          onEditPending={openPendingSettlementEdit}
          onVoidPending={voidPendingSettlement}
          selectedInvoiceRequestIds={selectedInvoiceRequestIds}
          onToggleInvoiceRequest={toggleInvoiceRequestSelection}
          onDownloadInvoiceRequest={downloadInvoiceRequest}
          onLinkInvoiceRequest={openInvoiceOrderLinker}
          invoiceRequestDownloading={invoiceRequestDownloading}
        />
      </section>

      <section className="premium-panel overflow-hidden">
        <div className="grid gap-3 border-b border-border-subtle px-4 py-4 lg:grid-cols-[1fr_auto_auto_auto] lg:items-end">
          <div>
            <h2 className="flex items-center gap-2 font-semibold"><History className="h-4 w-4 text-emerald-500" />月度已转账清单</h2>
            <p className="mt-1 text-xs text-muted-foreground">按财务确认时填写的实际转账日期归档。</p>
          </div>
          <label className="text-xs font-medium text-muted-foreground">
            转账月份
            <MonthSelect value={month} onChange={setMonth} className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          </label>
          <label className="relative text-xs font-medium text-muted-foreground">
            搜索
            <Search className="absolute bottom-2.5 left-3 h-4 w-4" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && fetchSettlements()}
              className="mt-1 w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground"
              placeholder="订单号、司机、路线、银行"
            />
          </label>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <button
              type="button"
              onClick={() => exportMonthlySettlements("xlsx")}
              disabled={Boolean(exporting)}
              className="premium-button border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-600"
            >
              {exporting === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Excel
            </button>
            <button
              type="button"
              onClick={() => exportMonthlySettlements("pdf")}
              disabled={Boolean(exporting)}
              className="premium-button border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-600"
            >
              {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              PDF
            </button>
            <button
              type="button"
              onClick={exportMonthlyPaymentStatements}
              disabled={Boolean(exporting) || !(data.history || []).length}
              className="premium-button border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"
            >
              {exporting === "payment-statements" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              支払明細書
            </button>
          </div>
        </div>
        <SettlementTable
          items={data.history || []}
          empty={`${month} 暂无已转账记录`}
          isFinance={isFinance}
          isAdmin={isAdmin}
          onUnlock={unlockSettlement}
          selectedInvoiceRequestIds={selectedInvoiceRequestIds}
          onToggleInvoiceRequest={toggleInvoiceRequestSelection}
          onDownloadInvoiceRequest={downloadInvoiceRequest}
          onLinkInvoiceRequest={openInvoiceOrderLinker}
          invoiceRequestDownloading={invoiceRequestDownloading}
        />
      </section>

      {isAdmin && (
        <FinanceAccountManager
          accounts={financeAccounts}
          form={financeForm}
          loading={loading}
          onFormChange={setFinanceForm}
          onSubmit={createFinanceAccount}
          onToggle={toggleFinanceAccount}
          onResetPassword={resetFinancePassword}
        />
      )}

      {transferTarget && createPortal(
        <TransferConfirmationModal
          settlement={transferTarget}
          transferDate={transferDate}
          maxTransferDate={todayJapan}
          loading={loading}
          onDateChange={setTransferDate}
          onClose={() => setTransferTarget(null)}
          onConfirm={confirmTransfer}
        />,
        document.body,
      )}

      {invoiceLinkModal && createPortal(
        <InvoiceRequestOrderLinkModal
          settlement={invoiceLinkModal}
          search={invoiceLinkSearch}
          results={invoiceLinkSearchResults}
          selectedOrderIds={invoiceLinkSelectedOrderIds}
          searching={invoiceLinkSearching}
          saving={invoiceLinkSaving}
          onSearchChange={setInvoiceLinkSearch}
          onSearch={searchInvoiceLinkOrders}
          onToggle={toggleInvoiceLinkOrderSelection}
          onClear={clearInvoiceLinkOrderSelection}
          onClose={closeInvoiceOrderLinker}
          onSave={saveInvoiceOrderLinks}
        />,
        document.body,
      )}
    </section>
  );
}

function InvoiceRequestOrderLinkModal({ settlement, search, results, selectedOrderIds, searching, saving, onSearchChange, onSearch, onToggle, onClear, onClose, onSave }) {
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="invoice-request-order-link-modal">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-border-subtle px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold"><Link2 className="h-5 w-5 text-primary" />绑定订单</h2>
            <p className="mt-1 text-xs text-muted-foreground">请求书 {settlement.request_no || settlement.order_no} · {settlement.status === "paid" ? "已转账" : "待财务处理"}</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="rounded-lg p-2 text-muted-foreground hover:bg-muted"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && onSearch()}
                className="w-full rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm text-foreground"
                placeholder="搜索司机、订单号、客户或路线关键字"
              />
            </label>
            <button type="button" onClick={onSearch} disabled={searching} className="premium-button inline-flex items-center justify-center gap-1.5 border border-primary/30 bg-primary/10 px-4 py-2 text-sm text-primary">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}搜索订单
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="font-semibold text-primary">已选择 {selectedOrderIds.length} 个订单</span>
            <button type="button" onClick={onClear} disabled={!selectedOrderIds.length || saving} className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground disabled:opacity-40">解除全部绑定</button>
          </div>
          <div className="mt-3 divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle">
            {results.length > 0 ? results.map((order) => (
              <label key={order.id} className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-primary/5">
                <input
                  type="checkbox"
                  checked={selectedOrderIds.includes(order.id)}
                  onChange={() => onToggle(order.id)}
                  className="mt-1 h-4 w-4 rounded border-border accent-primary"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2 text-sm">
                    <strong>{order.order_no}</strong>
                    {order.driver_name && <span className="text-muted-foreground">司机：{order.driver_name}</span>}
                    {order.customer_name && <span className="text-muted-foreground">客户：{order.customer_name}</span>}
                  </span>
                  <span className="mt-1 block break-words text-xs text-muted-foreground">{order.departure_date} {order.pickup_time} · {order.route?.summary || "未填写路线"}</span>
                </span>
              </label>
            )) : (
              <div className="px-5 py-8 text-center text-sm text-muted-foreground">输入关键字搜索订单；留空可查看最近订单</div>
            )}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">保存后只更新请求书的订单关联，不修改请求书金额、转账状态或原订单。</p>
        </div>
        <div className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button type="button" onClick={onClose} disabled={saving} className="premium-button border border-border bg-background px-4 py-2">取消</button>
          <button type="button" onClick={onSave} disabled={saving} className="premium-button inline-flex items-center justify-center gap-1.5 bg-primary px-5 py-2 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}{saving ? "保存中" : "保存绑定"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SettlementFormModal({ settlementProps }) {
  const {
    aiParsing,
    aiReview,
    bankRawText,
    form,
    loading,
    modal,
    closeModal,
    parseBankAccountText,
    setBankRawText,
    setForm,
    submitSettlement,
    submitPendingSettlementUpdate,
    submitAdditionalExpense,
    submitSettlementUpdate,
    updateBankField,
  } = settlementProps;
  if (!modal) return null;
  const order = modal.prepared?.order || {};
  const isPaidEdit = modal.mode === "edit";
  const isPendingEdit = modal.mode === "pending-edit";
  const isAdditionalExpense = modal.mode === "additional-expense" || modal.settlement?.settlement_type === "additional_expense";
  const isInvoiceRequest = modal.settlement?.settlement_type === "invoice_request";
  const originalPrice = Number(order.original_dispatch_price || 0);
  const actualPrice = Number(form.settlement_amount || 0);
  const needsReason = isAdditionalExpense || (!isInvoiceRequest && actualPrice !== originalPrice);
  const bankAccountSource = modal.prepared?.bank_account_source || {};
  const isPhoneHistoryPrefill = !isPaidEdit && !isPendingEdit && bankAccountSource.type === "settlement_history";
  const isLegacyNameHistoryPrefill = !isPaidEdit && !isPendingEdit && bankAccountSource.type === "legacy_driver_name_history";

  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-3 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="flex max-h-[94vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase text-primary">{isInvoiceRequest ? "Invoice Request" : isAdditionalExpense ? "Additional Driver Expense" : isPaidEdit ? "Unlocked Audit Edit" : isPendingEdit ? "Pending Settlement Edit" : "New Settlement"}</p>
            <h2 className="mt-1 text-xl font-semibold">{isInvoiceRequest ? "修改请求书结算" : isAdditionalExpense ? "单独结算司机附加费用" : isPaidEdit ? "修改已转账记录" : isPendingEdit ? "修改待转账记录" : "提交财务结算"}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{order.order_no} · {order.driver_name} · {order.route?.summary}</p>
          </div>
          <button type="button" onClick={closeModal} className="icon-button" aria-label="关闭"><X className="h-5 w-5" /></button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-5">
          {isPaidEdit && (
            <div className="mb-5 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              该记录已经转账。保存后仍保持已转账，原值与新值会永久写入修改历史，原派单价格不会改变。
            </div>
          )}
          {isPendingEdit && (
            <div className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-700">
              财务尚未转账，可以修正实际结算金额或银行资料；修改内容会保留在操作历史中。
            </div>
          )}
          {isPhoneHistoryPrefill && (
            <div
              className="mb-5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800"
              data-testid="settlement-phone-history-prefill"
            >
              已按司机电话 {bankAccountSource.phone || order.driver_phone || "-"} 自动带入最近结算资料
              {bankAccountSource.order_no ? `（订单 ${bankAccountSource.order_no}）` : ""}。提交前仍可修改并核对。
            </div>
          )}
          {isLegacyNameHistoryPrefill && (
            <div
              className="mb-5 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-800"
              data-testid="settlement-legacy-name-history-prefill"
            >
              旧结算记录未保存司机电话，已按司机姓名自动带入最近结算资料
              {bankAccountSource.order_no ? `（订单 ${bankAccountSource.order_no}）` : ""}。请核对后提交；本次会绑定当前司机电话。
            </div>
          )}
          {isAdditionalExpense && (
            <div className="mb-5 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary" data-testid="additional-expense-original-settlement-guard">
              本笔费用直接单独转账给司机，只进入附加费用账本；不会改动原订单派单价、主结算金额或主结算状态。
            </div>
          )}
          {isInvoiceRequest && (
            <div className="mb-5 rounded-lg border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary" data-testid="invoice-request-independent-settlement-guard">
              请求书是独立的待转账记录；金额可修正，不会改动任何订单的派单价或主结算。
            </div>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <section className="space-y-4">
              <div className={`grid gap-3 ${isInvoiceRequest ? "grid-cols-1" : "grid-cols-2"}`}>
                {!isInvoiceRequest && <ReadOnlyMoney label={isAdditionalExpense ? "原订单派单价格（只读）" : "原派单价格"} value={originalPrice} />}
                <label className="text-sm font-medium">
                  {isInvoiceRequest ? "请求书金额" : isAdditionalExpense ? "附加费用金额" : "实际结算金额"}
                  <input
                    type="number"
                    min={isAdditionalExpense || isInvoiceRequest ? "1" : "0"}
                    step="1"
                    value={form.settlement_amount}
                    onChange={(event) => setForm((current) => ({ ...current, settlement_amount: event.target.value }))}
                    className="premium-input mt-1"
                    required
                  />
                </label>
              </div>
              {isAdditionalExpense && (
                <label className="block text-sm font-medium">
                  费用类型 <span className="text-destructive">*</span>
                  <select
                    value={form.expense_category}
                    onChange={(event) => setForm((current) => ({ ...current, expense_category: event.target.value }))}
                    disabled={isPaidEdit || isPendingEdit}
                    className="premium-input mt-1"
                  >
                    <option value="parking">停车费</option>
                    <option value="overtime">加班费</option>
                    <option value="toll">高速费</option>
                    <option value="other">其他费用</option>
                  </select>
                </label>
              )}
              <label className="block text-sm font-medium">
                {isInvoiceRequest ? "请求书备注" : isAdditionalExpense ? "费用说明" : "金额调整原因"} {needsReason && <span className="text-destructive">*</span>}
                <textarea
                  value={form.adjustment_reason}
                  onChange={(event) => setForm((current) => ({ ...current, adjustment_reason: event.target.value }))}
                  className="premium-input mt-1 min-h-20"
                  placeholder={isInvoiceRequest ? "可选填请求书说明" : isAdditionalExpense ? "例如：羽田机场停车费，凭证已核对" : needsReason ? "实际结算金额与原派单价不同，必须填写原因" : "金额一致时可不填"}
                />
              </label>

              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-2 font-semibold"><Bot className="h-4 w-4 text-primary" />AI智能识别银行文字</div>
                <textarea
                  value={bankRawText}
                  onChange={(event) => setBankRawText(event.target.value)}
                  className="premium-input mt-3 min-h-32 text-sm"
                  placeholder="粘贴任意格式的银行账户文字。原始文字不会保存到结算记录。"
                />
                <button
                  type="button"
                  onClick={parseBankAccountText}
                  disabled={aiParsing || !bankRawText.trim()}
                  className="premium-button mt-3 bg-primary px-4 py-2 text-primary-foreground"
                >
                  {aiParsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bot className="h-4 w-4" />}
                  {aiParsing ? "AI识别中" : "AI识别并填充"}
                </button>
                {aiReview.length > 0 && (
                  <div className="mt-3 space-y-1 text-xs text-amber-600">
                    {aiReview.map((item) => <p key={item}>请确认：{item}</p>)}
                  </div>
                )}
              </div>
            </section>

            <section className="grid content-start gap-3 sm:grid-cols-2">
              <BankField
                label="银行名"
                value={form.bank_account.bank_name}
                onChange={(value) => updateBankField("bank_name", value)}
                listId="settlement-common-bank-names"
                options={["三菱UFJ銀行", "みずほ銀行", "三井住友銀行", "ゆうちょ銀行"]}
              />
              <BankField label="支店名" value={form.bank_account.branch_name} onChange={(value) => updateBankField("branch_name", value)} />
              <BankField label="支店番号" value={form.bank_account.branch_code} onChange={(value) => updateBankField("branch_code", value)} inputMode="numeric" />
              <label className="text-sm font-medium">
                账户类型
                <select value={form.bank_account.account_type} onChange={(event) => updateBankField("account_type", event.target.value)} className="premium-input mt-1">
                  <option value="普通">普通</option>
                  <option value="当座">当座</option>
                  <option value="貯蓄">貯蓄</option>
                  <option value="其他">其他</option>
                </select>
              </label>
              <BankField label="口座番号" value={form.bank_account.account_number} onChange={(value) => updateBankField("account_number", value)} inputMode="numeric" />
              <BankField label="口座名义人" value={form.bank_account.account_holder} onChange={(value) => updateBankField("account_holder", value)} />

              <div className="sm:col-span-2 rounded-lg border border-border-subtle bg-muted/30 px-4 py-3 text-xs text-muted-foreground">
                保存前请逐项核对。AI只负责填入表单，不会自动确认结算或转账。
              </div>
            </section>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button type="button" onClick={closeModal} className="premium-button border border-border bg-background px-4 py-2">取消</button>
          <button
            type="button"
            onClick={modal.mode === "additional-expense" ? submitAdditionalExpense : isPaidEdit ? submitSettlementUpdate : isPendingEdit ? submitPendingSettlementUpdate : submitSettlement}
            disabled={loading || (needsReason && !form.adjustment_reason.trim())}
            className="premium-button bg-primary px-5 py-2 text-primary-foreground"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : isPaidEdit ? <History className="h-4 w-4" /> : isPendingEdit ? <Pencil className="h-4 w-4" /> : <WalletCards className="h-4 w-4" />}
            {modal.mode === "additional-expense" ? "提交附加费用" : isPaidEdit ? "保存审计修改" : isPendingEdit ? "保存待转账修改" : "提交财务处理"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SettlementTable({
  items,
  empty,
  isPending = false,
  isFinance = false,
  isAdmin = false,
  onMarkPaid,
  onUnlock,
  onEditPending,
  onVoidPending,
  selectedInvoiceRequestIds = [],
  onToggleInvoiceRequest,
  onDownloadInvoiceRequest,
  onLinkInvoiceRequest,
  invoiceRequestDownloading = "",
}) {
  const [expandedId, setExpandedId] = useState("");
  if (!items.length) return <div className="px-5 py-10 text-center text-sm text-muted-foreground">{empty}</div>;
  return (
    <>
      <div className="divide-y divide-border-subtle md:hidden">
        {items.map((item) => (
          <MobileSettlementRow
            key={item.id}
            item={item}
            expanded={expandedId === item.id}
            isPending={isPending}
            isFinance={isFinance}
            isAdmin={isAdmin}
            onExpand={() => setExpandedId((current) => current === item.id ? "" : item.id)}
            onMarkPaid={onMarkPaid}
            onUnlock={onUnlock}
            onEditPending={onEditPending}
            onVoidPending={onVoidPending}
            selected={selectedInvoiceRequestIds.includes(item.id)}
            onToggleInvoiceRequest={onToggleInvoiceRequest}
            onDownloadInvoiceRequest={onDownloadInvoiceRequest}
            onLinkInvoiceRequest={onLinkInvoiceRequest}
            invoiceRequestDownloading={invoiceRequestDownloading}
          />
        ))}
      </div>
      <div className="hidden overflow-x-auto md:block">
      <table className="w-full min-w-[1180px] text-sm">
        <thead className="bg-muted/50 text-xs text-muted-foreground">
          <tr>
            <th className="min-w-[190px] px-4 py-3 text-left">订单 / 司机</th>
            <th className="px-4 py-3 text-left">日期 / 路线</th>
            <th className="px-4 py-3 text-right">原派单价</th>
            <th className="px-4 py-3 text-right">结算金额</th>
            <th className="px-4 py-3 text-left">转账账户</th>
            <th className="min-w-[130px] px-4 py-3 text-left">转账信息</th>
            <th className={`${isAdmin ? "min-w-[190px]" : "min-w-[100px]"} px-4 py-3 text-right`}>操作</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-subtle">
          {items.map((item) => (
            <SettlementRow
              key={item.id}
              item={item}
              expanded={expandedId === item.id}
              isPending={isPending}
              isFinance={isFinance}
              isAdmin={isAdmin}
              onExpand={() => setExpandedId((current) => current === item.id ? "" : item.id)}
              onMarkPaid={onMarkPaid}
              onUnlock={onUnlock}
              onEditPending={onEditPending}
              onVoidPending={onVoidPending}
              selected={selectedInvoiceRequestIds.includes(item.id)}
              onToggleInvoiceRequest={onToggleInvoiceRequest}
              onDownloadInvoiceRequest={onDownloadInvoiceRequest}
              onLinkInvoiceRequest={onLinkInvoiceRequest}
              invoiceRequestDownloading={invoiceRequestDownloading}
            />
          ))}
        </tbody>
      </table>
      </div>
    </>
  );
}

function MobileSettlementRow({ item, expanded, isPending, isFinance, isAdmin, onExpand, onMarkPaid, onUnlock, onEditPending, onVoidPending, selected, onToggleInvoiceRequest, onDownloadInvoiceRequest, onLinkInvoiceRequest, invoiceRequestDownloading }) {
  return (
    <article className="space-y-3 px-4 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-semibold">
            {item.settlement_type === "invoice_request" && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleInvoiceRequest?.(item.id)}
                aria-label={`选择请求书 ${item.request_no || item.order_no}`}
                className="h-4 w-4"
              />
            )}
            <span>{item.order_no} · {item.driver_name}</span>
          </div>
          <div className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${item.settlement_type === "additional_expense" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{getSettlementKindLabel(item)}</div>
          <div className="mt-1 break-words text-xs text-muted-foreground">{item.departure_date} {item.pickup_time} · {item.route?.summary}</div>
        </div>
        <StatusChip status={item.status} />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {item.settlement_type !== "invoice_request" && <div><span className="block text-xs text-muted-foreground">{item.settlement_type === "additional_expense" ? "原订单派单价" : "原派单价"}</span><strong>{formatYen(item.original_dispatch_price)}</strong></div>}
        <div><span className="block text-xs text-muted-foreground">{item.settlement_type === "invoice_request" ? "请求书金额" : item.settlement_type === "additional_expense" ? "附加费用" : "实际结算"}</span><strong className="text-emerald-600">{formatYen(item.settlement_amount)}</strong></div>
      </div>
      {item.adjustment_reason && <p className="text-xs text-amber-600">调整原因：{item.adjustment_reason}</p>}
      <div className="break-words border-t border-border-subtle pt-3 text-xs leading-5 text-muted-foreground">
        {formatAccount(item.bank_account)}
      </div>
      {!isPending && (
        <p className="text-xs text-muted-foreground">转账：{item.transfer_date} · {item.confirmed_by_name || item.confirmed_by_email}</p>
      )}
      {isPending && item.payable === false && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">暂不可转账：{item.blocking_reason}</p>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {item.settlement_type === "invoice_request" && (
          <>
            <button type="button" onClick={() => onDownloadInvoiceRequest?.(item)} disabled={Boolean(invoiceRequestDownloading)} className="premium-button border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
              {invoiceRequestDownloading === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}单独下载
            </button>
            {(isAdmin || isFinance) && (
              <button type="button" onClick={() => onLinkInvoiceRequest?.(item)} className="premium-button border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
                <Link2 className="h-3.5 w-3.5" />绑定订单
              </button>
            )}
          </>
        )}
        {isPending && isFinance && (
          <button type="button" onClick={() => onMarkPaid(item)} disabled={item.payable === false} className="premium-button bg-emerald-600 px-4 py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-40">
            <BadgeCheck className="h-4 w-4" />确认已转账
          </button>
        )}
        {isPending && isAdmin && (
          <>
            <button type="button" onClick={() => onEditPending(item)} disabled={item.payable === false} className="premium-button border border-border bg-background px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-40">
              <Pencil className="h-3.5 w-3.5" />修改
            </button>
            <button type="button" onClick={() => onVoidPending(item)} className="premium-button border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <XCircle className="h-3.5 w-3.5" />撤销
            </button>
          </>
        )}
        {!isPending && isAdmin && (
          <button type="button" onClick={() => onUnlock(item)} className="premium-button border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <KeyRound className="h-3.5 w-3.5" />解锁修改
          </button>
        )}
        {!isPending && item.revision_count > 0 && (
          <button type="button" onClick={onExpand} className="premium-button border border-border bg-background px-3 py-2 text-xs">
            <ChevronDown className={`h-3.5 w-3.5 transition ${expanded ? "rotate-180" : ""}`} />修改历史
          </button>
        )}
      </div>
      {expanded && <RevisionHistory revisions={item.revisions || []} />}
    </article>
  );
}

function SettlementRow({ item, expanded, isPending, isFinance, isAdmin, onExpand, onMarkPaid, onUnlock, onEditPending, onVoidPending, selected, onToggleInvoiceRequest, onDownloadInvoiceRequest, onLinkInvoiceRequest, invoiceRequestDownloading }) {
  return (
    <>
      <tr className="align-top hover:bg-muted/30">
        <td className="px-4 py-3">
          <div className="flex items-center gap-2 font-semibold">
            {item.settlement_type === "invoice_request" && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleInvoiceRequest?.(item.id)}
                aria-label={`选择请求书 ${item.request_no || item.order_no}`}
                className="h-4 w-4 shrink-0"
              />
            )}
            <span className={item.settlement_type === "invoice_request" ? "whitespace-nowrap" : ""}>{item.order_no}</span>
          </div>
          <div className="mt-1 text-muted-foreground">{item.driver_name}</div>
          <div className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${item.settlement_type === "additional_expense" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>{getSettlementKindLabel(item)}</div>
          <StatusChip status={item.status} />
        </td>
        <td className="max-w-sm px-4 py-3">
          <div>{item.departure_date} {item.pickup_time}</div>
          <div className="mt-1 break-words text-xs text-muted-foreground">{item.route?.summary}</div>
        </td>
        <td className="px-4 py-3 text-right font-medium tabular-nums">
          {item.settlement_type === "invoice_request" ? "—" : formatYen(item.original_dispatch_price)}
          {item.settlement_type === "additional_expense" && <div className="mt-1 text-[11px] font-normal text-muted-foreground">原订单价格（只读）</div>}
        </td>
        <td className="px-4 py-3 text-right font-semibold text-emerald-600 tabular-nums">
          {formatYen(item.settlement_amount)}
          {item.adjustment_reason && <div className="mt-1 max-w-36 text-xs font-normal text-amber-600">{item.adjustment_reason}</div>}
        </td>
        <td className="max-w-sm px-4 py-3 text-xs leading-5">
          <div className="break-words text-muted-foreground">{formatAccount(item.bank_account)}</div>
        </td>
        <td className="min-w-[130px] px-4 py-3 text-xs">
          {isPending ? (
            item.payable === false ? (
              <span className="block max-w-56 text-destructive">暂不可转账：{item.blocking_reason}</span>
            ) : (
              <span className="text-amber-600">等待财务确认</span>
            )
          ) : (
            <>
              <div className="whitespace-nowrap font-medium">{item.transfer_date}</div>
              <div className="mt-1 whitespace-nowrap text-muted-foreground">{item.confirmed_by_name || item.confirmed_by_email}</div>
              {item.revision_count > 0 && <div className="mt-1 text-destructive">已审计修改 {item.revision_count} 次</div>}
            </>
          )}
        </td>
        <td className={`${isAdmin ? "min-w-[190px]" : "min-w-[100px]"} px-4 py-3 text-right whitespace-nowrap`}>
          {item.settlement_type === "invoice_request" && (
            <div className="mb-2 flex flex-nowrap justify-end gap-2">
              <button type="button" onClick={() => onDownloadInvoiceRequest?.(item)} disabled={Boolean(invoiceRequestDownloading)} className="premium-button inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
                {invoiceRequestDownloading === item.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}下载
              </button>
              {(isAdmin || isFinance) && (
                <button type="button" onClick={() => onLinkInvoiceRequest?.(item)} className="premium-button inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
                  <Link2 className="h-3.5 w-3.5" />绑定订单
                </button>
              )}
            </div>
          )}
          {isPending && isFinance && (
            <label className={`inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 font-semibold text-emerald-600 ${item.payable === false ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}>
              <input type="checkbox" checked={false} disabled={item.payable === false} onChange={() => onMarkPaid(item)} className="h-4 w-4" />
              已转账
            </label>
          )}
          {isPending && isAdmin && (
            <div className="flex flex-nowrap justify-end gap-2">
              <button type="button" onClick={() => onEditPending(item)} disabled={item.payable === false} className="premium-button inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-border bg-background px-3 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-40">
                <Pencil className="h-3.5 w-3.5" />修改
              </button>
              <button type="button" onClick={() => onVoidPending(item)} className="premium-button inline-flex items-center justify-center gap-1.5 whitespace-nowrap border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                <XCircle className="h-3.5 w-3.5" />撤销
              </button>
            </div>
          )}
          {!isPending && isAdmin && (
            <button type="button" onClick={() => onUnlock(item)} className="premium-button border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              <KeyRound className="h-3.5 w-3.5" />解锁修改
            </button>
          )}
          {!isPending && item.revision_count > 0 && (
            <button type="button" onClick={onExpand} className="premium-button ml-2 border border-border bg-background px-2 py-2 text-xs">
              <ChevronDown className={`h-3.5 w-3.5 transition ${expanded ? "rotate-180" : ""}`} />历史
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-muted/20">
          <td colSpan="7" className="px-5 py-4">
            <RevisionHistory revisions={item.revisions || []} />
          </td>
        </tr>
      )}
    </>
  );
}

function RevisionHistory({ revisions }) {
  if (!revisions.length) return <p className="text-xs text-muted-foreground">暂无修改历史</p>;
  return (
    <div className="space-y-3">
      {revisions.map((revision) => (
        <div key={`${revision.revision_no}-${revision.changed_at}`} className="rounded-lg border border-border-subtle bg-background px-4 py-3 text-xs">
          <div className="flex flex-wrap justify-between gap-2 font-semibold">
            <span>第 {revision.revision_no} 次修改{revision.phase === "pending" ? "（转账前）" : "（转账后）"} · {revision.changed_by_name || revision.changed_by_email}</span>
            <span>{revision.changed_at}</span>
          </div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            <div><span className="text-muted-foreground">修改前：</span>{formatYen(revision.before?.settlement_amount)} · {formatAccount(revision.before?.bank_account)}</div>
            <div><span className="text-muted-foreground">修改后：</span>{formatYen(revision.after?.settlement_amount)} · {formatAccount(revision.after?.bank_account)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TransferConfirmationModal({ settlement, transferDate, maxTransferDate, loading, onDateChange, onClose, onConfirm }) {
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">确认已转账</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {settlement.settlement_type === "invoice_request"
                ? "确认后请求书将按实际转账日归档，不改变任何订单结算。"
                : settlement.settlement_type === "additional_expense"
                ? "确认后只锁定本笔附加费用，不改变原订单主结算。"
                : "确认后立即锁定，司机端只更新为“已结算”。"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="icon-button" aria-label="关闭"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-4 space-y-3 rounded-lg border border-border-subtle bg-muted/25 p-4 text-sm">
          <div className="flex justify-between"><span>订单</span><strong>{settlement.order_no}</strong></div>
          <div className="flex justify-between"><span>司机</span><strong>{settlement.driver_name}</strong></div>
          <div className="flex justify-between"><span>金额</span><strong className="text-emerald-600">{formatYen(settlement.settlement_amount)}</strong></div>
          {settlement.settlement_type === "additional_expense" && <div className="flex justify-between"><span>费用类型</span><strong>{getSettlementKindLabel(settlement)}</strong></div>}
          <div className="border-t border-border-subtle pt-3 text-xs leading-5">{formatAccount(settlement.bank_account)}</div>
        </div>
        <label className="mt-4 block text-sm font-medium">
          实际转账日期（日本时间）
          <input type="date" value={transferDate} max={maxTransferDate} onChange={(event) => onDateChange(event.target.value)} className="premium-input mt-1" />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="premium-button border border-border bg-background px-4 py-2">取消</button>
          <button type="button" onClick={onConfirm} disabled={loading || !transferDate} className="premium-button bg-emerald-600 px-4 py-2 text-white">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}确认已转账
          </button>
        </div>
      </div>
    </div>
  );
}

function FinanceAccountManager({ accounts, form, loading, onFormChange, onSubmit, onToggle, onResetPassword }) {
  return (
    <section className="premium-panel overflow-hidden">
      <div className="border-b border-border-subtle px-4 py-4">
        <h2 className="flex items-center gap-2 font-semibold"><UserPlus className="h-4 w-4 text-primary" />财务账号管理</h2>
        <p className="mt-1 text-xs text-muted-foreground">财务账号只能进入网页版结算页面和确认转账。</p>
      </div>
      <form onSubmit={onSubmit} className="grid gap-3 border-b border-border-subtle p-4 md:grid-cols-[1fr_1.4fr_1fr_auto] md:items-end">
        <FinanceField name="finance-name" label="姓名" value={form.name} onChange={(value) => onFormChange((current) => ({ ...current, name: value }))} />
        <FinanceField name="finance-email" label="登录邮箱" value={form.email} onChange={(value) => onFormChange((current) => ({ ...current, email: value }))} type="email" />
        <FinanceField name="finance-password" label="初始密码" value={form.password} onChange={(value) => onFormChange((current) => ({ ...current, password: value }))} type="password" minLength={6} />
        <button type="submit" disabled={loading} className="premium-button bg-primary px-4 py-2 text-primary-foreground">
          <UserPlus className="h-4 w-4" />创建财务账号
        </button>
      </form>
      <div className="divide-y divide-border-subtle">
        {accounts.length === 0 ? <div className="px-4 py-8 text-center text-sm text-muted-foreground">暂无财务账号</div> : accounts.map((account) => (
          <div key={account.id} className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-semibold">{account.name || "未命名财务"}</div>
              <div className="text-xs text-muted-foreground">{account.email} · {account.status === "approved" ? "可登录" : "已停用"}</div>
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => onResetPassword(account)} className="premium-button border border-border bg-background px-3 py-2 text-xs"><KeyRound className="h-3.5 w-3.5" />重置密码</button>
              <button type="button" onClick={() => onToggle(account)} className="premium-button border border-border bg-background px-3 py-2 text-xs">{account.status === "approved" ? "停用" : "启用"}</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function SummaryTile({ label, value, amount, tone = "default", icon: Icon }) {
  const toneClass = tone === "amber" ? "text-amber-600" : tone === "emerald" ? "text-emerald-600" : "text-foreground";
  return (
    <div className="rounded-lg border border-border-subtle bg-card px-4 py-3">
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className={`mt-1 text-lg font-semibold ${toneClass}`}>{value}</div>
      {amount !== undefined && <div className="mt-1 text-sm font-medium tabular-nums text-muted-foreground">{formatYen(amount)}</div>}
    </div>
  );
}

function StatusChip({ status }) {
  const paid = status === "paid";
  return (
    <span className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${paid ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>
      {paid ? <Check className="h-3 w-3" /> : <WalletCards className="h-3 w-3" />}
      {paid ? "已转账" : "待转账"}
    </span>
  );
}

function ReadOnlyMoney({ label, value }) {
  return (
    <div className="rounded-lg border border-border-subtle bg-muted/30 px-3 py-2">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold tabular-nums">{formatYen(value)}</div>
    </div>
  );
}

function BankField({ label, value, onChange, inputMode, type = "text", listId, options = [] }) {
  return (
    <label className="text-sm font-medium">
      {label}
      <input type={type} inputMode={inputMode} list={listId} value={value || ""} onChange={(event) => onChange(event.target.value)} className="premium-input mt-1" />
      {listId && (
        <datalist id={listId}>
          {options.map((option) => <option key={option} value={option} />)}
        </datalist>
      )}
    </label>
  );
}

function FinanceField({ name, label, value, onChange, type = "text", minLength }) {
  return (
    <label className="text-sm font-medium">
      {label}
      <input
        name={name}
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        required
        minLength={minLength}
        className="premium-input mt-1"
      />
    </label>
  );
}
